import { Button, Modal, Radio, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons';
import CountdownTimer from "../ExamRoom/CountdownTimer";
import { useDispatch } from "react-redux";
import { getData } from "../../../actions/apiCall";
import { useNavigate, useParams } from "react-router-dom";
import { decryptData, encryptData } from "../../../actions/crypto";
import { putData } from "../../../actions/developer";
import moment from "moment";
import { examConfig } from "../../../config/examConfig";

const { Title } = Typography;

const MCQuestions = ({ testDetails, onSubmitMCQ }) => {
    const { id } = useParams();
    const [selected, setSelected] = useState<any>(null);
    const [questionData, setQuestionData] = useState<any>([]);
    const [activeSeat, setActiveSeat] = useState<string>("");
    const [currentQuestion, setCurrentQuestion] = useState<any>(null);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
    const [totalTime, setTotalTime] = useState<number>(0);
    const [options, setOptions] = useState<any>([]);

    const dispatch = useDispatch();

    const getTotalTime = () => {
        const mcqQuestions: any = testDetails?.mcqQuestions;
        if (mcqQuestions.length > 0) {
            return mcqQuestions.reduce((acc: number, ele: any) => acc + ele.timeRequired, 0);
        }
        return 0;
    }

    const checkMcqTimeCompleted = () => {
        if (!examConfig.enableTimer) return; // Skip if timer is disabled

        if (testDetails?.testStage === "codding") {
            onSubmitMCQ();
            return;
        }
        const mcqStart = testDetails?.mcqStartedAt;
        if (!mcqStart) {
            location.href = `/instructions/${id}`;
        };

        const totalTime = getTotalTime(); // in minutes
        const endTime = moment(mcqStart).add(totalTime, 'minutes');
        const now = moment();

        if (now.isAfter(endTime)) {
            handleSubmit();
        }
    };

    useEffect(() => {
        if (testDetails) {
            if (examConfig.enableTimer) {
                checkMcqTimeCompleted();
            }
            const mcqQuestions: any = testDetails?.mcqQuestions;
            if (mcqQuestions.length > 0) {
                const totalTime = mcqQuestions.reduce((acc: number, ele: any) => acc + ele.timeRequired, 0);
                setTotalTime(totalTime);
                loadSection(mcqQuestions[currentSectionIndex]?.sectionId);
            }
        }
    }, [testDetails]);

    useEffect(() => {
        if (activeSeat) {
            const data = questionData.find((ele: any) => ele._id === activeSeat);
            if (data) {
                setCurrentQuestion(data);
                const getLocal = async () => {
                    const optionList = await getMcqData();
                    setSelected(optionList[data._id] || null);
                }
                getLocal();
            }
        }
    }, [activeSeat, questionData]);

    const loadSection = async (sectionId: string) => {
        if (!id || !sectionId) return;
        const resData = await dispatch(getData(`/accel-test/${id}/${encryptData(sectionId)}/mcq`));
        if (resData.code === 200) {
            const data = decryptData(resData.data);
            const object = {};
            data.forEach(element => {
                if (element.selectedOptions !== undefined) {
                    object[element._id] = element.selectedOptions
                }
            });

            localStorage.setItem('mcqData', JSON.stringify(encryptData(object)))
            setQuestionData(data);
            setActiveSeat(data[0]?._id || "");
            setCurrentIndex(0);
        }
    };

    const handleNext = () => {
        if (currentIndex < questionData.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setActiveSeat(questionData[currentIndex + 1]._id);
        } else {
            const mcqQuestions: any = testDetails?.mcqQuestions;
            if (currentSectionIndex < mcqQuestions.length - 1) {
                setCurrentSectionIndex(currentSectionIndex + 1);
                loadSection(mcqQuestions[currentSectionIndex + 1].sectionId);
            }
        }
    };

    const getMcqData = async () => {
        const mcqData = localStorage.getItem('mcqData');
        if (!mcqData) {
            setOptions({}); // Ensure state is updated even when no data exists
            return {};
        }
        try {
            const decryptedData = await decryptData(JSON.parse(mcqData));
            setOptions(decryptedData); // Set state with decrypted data
            return decryptedData;
        } catch (error) {
            setOptions({});
            return {};
        }
    };

    const setAnsInLocal = async (questionId: string, option: string) => {
        const data = await getMcqData();
        const updatedData = { ...data, [questionId]: option };
        localStorage.setItem('mcqData', JSON.stringify(encryptData(updatedData)));
        setOptions(updatedData); // Update state with the latest answer
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setActiveSeat(questionData[currentIndex - 1]._id);
        } else {
            if (currentSectionIndex > 0) {
                setCurrentSectionIndex(currentSectionIndex - 1);
                loadSection(testDetails.mcqQuestions[currentSectionIndex - 1].sectionId);
            }
        }
    };

    const isLastQuestion = () => {
        const mcqQuestions: any = testDetails?.mcqQuestions || [];
        return currentSectionIndex === mcqQuestions.length - 1 && currentIndex === questionData.length - 1;
    };

    const handleOptionSelect = async (questionId, option) => {
        setSelected(option);
        const resData = await dispatch(putData(`/accel-test/${id}`, { data: encryptData({ option, questionId: questionId }) }, false));
        if (resData) {
            setAnsInLocal(questionId, option);
        }
    }

    const handleSubmit = async () => {
        try {
            const isCoding = testDetails?.coddingQuestions && (testDetails?.coddingQuestions?.length > 0);
            const resData = await dispatch(putData(`/accel-test/test/started/${id}`, {
                data: encryptData({
                    mcqEndedAt: new Date(),
                    coddingStartedAt: isCoding ? new Date() : null,
                    testStage: isCoding ? 'codding' : 'completed'
                })
            }), false);
            if (resData) {
                onSubmitMCQ();
            }
        } catch (error) {
            console.log(error);
        }
    }

    const mCQTimeISDone = () => {
        const modal = Modal.confirm({
            title: "MCQ Time's up!",
            content: "Your MCQ time is up. The test will now be submitted automatically. Please wait while the next section starts.",
            cancelButtonProps: { style: { display: 'none' } }, // Hide the cancel button
            okButtonProps: { style: { display: 'none' } }, // Hide the ok button
        });
        setTimeout(() => {
            modal.destroy(); // Close the modal after 4 seconds 
            handleSubmit();
        }, 4000); // Automatically submit after 1 second
    };

    return (
        <div className="bg-[#F3F7F7] flex h-screen flex-col overflow-hidden">
            <div className="flex justify-between items-center border-b border-gray-200 p-2">
                <img src={require('./../../../assets/logo/logo.png')} alt="logo" className="w-40" />
                {examConfig.enableTimer && (
                    <CountdownTimer
                        durationMinutes={totalTime}
                        startTime={testDetails?.mcqStartedAt}
                        timeUp={() => mCQTimeISDone()}
                    />
                )}
                <Space>
                    {currentIndex > 0 || currentSectionIndex > 0 ? (
                        <Button type="default" icon={<DoubleLeftOutlined />} onClick={handlePrev}>
                            Previous
                        </Button>
                    ) : null}

                    {isLastQuestion() ? (
                        <Button type="primary" className="items-center" onClick={() => handleSubmit()}>
                            Submit
                        </Button>
                    ) : (
                        <Button type="primary" icon={<DoubleRightOutlined />} onClick={handleNext}>
                            Next
                        </Button>
                    )}
                </Space>
            </div>

            <div className="bg-white p-2">{currentQuestion?.section?.name}</div>

            <div className="h-full flex justify-between items-center p-4">
                <div className="h-full p-4 mr-4 bg-white">
                    <div className="grid grid-cols-4 gap-5 pr-4">
                        {questionData.map((ele, index) => (
                            <div
                                key={ele._id}
                                onClick={() => {
                                    setActiveSeat(ele._id)
                                    setCurrentIndex(index)
                                }}
                                className={`w-12 h-12 flex items-center  justify-center rounded-full text-lg font-semibold cursor-pointer hover:border-2 hover:border-red-500
                                    ${ele._id === activeSeat ? "border-2 border-red-500 text-red-500" : "border border-gray-400 text-gray-700 text-black"}
                                    ${options[ele._id] ? "bg-red-800 text-white border-2 border-red-500" : "bg-white text-black"}
                                `}
                            >
                                {index + 1}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="w-full flex justify-between h-full p-7 bg-white">
                    <div className="w-2/4">
                        <Title level={4}>{currentQuestion?.mcqQuestion?.question}</Title>
                    </div>

                    <div className="w-2/4">
                        <div className="max-w-sm mx-auto mt-10 space-y-3">
                            <h3 className="font-semibold">Select only one</h3>
                            {currentQuestion?.mcqQuestion?.options.map((option) => (
                                <label
                                    key={option?.option}
                                    className={`flex items-center p-4 rounded-lg cursor-pointer border hover:border-red-500
                                        ${selected === option?.option ? "bg-red-800 text-white border-red-800" : "border-gray-300 text-gray-800"}
                                    `}
                                >
                                    <Radio
                                        className="hidden"
                                        checked={selected === option?.option}
                                        onChange={() => handleOptionSelect(currentQuestion?._id, option?.option)}
                                    />
                                    <div
                                        className={`w-5 h-5 flex items-center justify-center border-2 rounded-full mr-3
                                            ${selected === option?.option ? "border-red-800 bg-red-800 text-white" : "border-gray-400"}
                                        `}
                                    >
                                        {selected === option?.option && <div className="w-2.5 h-2.5 bg-white rounded-full"></div>}
                                    </div>
                                    <span className="text-sm">{option?.option}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MCQuestions;